import { apiClient, ApiResponse } from '@packages/api-client';

// 资源树节点类型
export interface ResourceTreeNode {
  id: number;
  name: string;
  level: number;
  ontologyId: number;
  type: 'DIRECTORY' | 'ONTOLOGY';
  children: ResourceTreeNode[];
  canView: boolean;
  canEdit: boolean;
  canManage: boolean;
}

// 资源相关API
export class ResourceApi {
  /**
   * 获取资源树
   * @returns Promise<ResourceTreeNode[]>
   */
  static getResourceTree(): Promise<ResourceTreeNode[]> {
    return apiClient.get<ResourceTreeNode[]>('/resources/tree')
      .then(response => response.data);
  }

  /**
   * 创建资源（统一接口）
   * @param name 资源名称
   * @param parentId 父目录ID，可选
   * @param isDirectory 是否1是目录
   * @returns Promise<ResourceTreeNode>
   */
  static async createResource(name: string, parentId?: number, isDirectory: boolean = false): Promise<ResourceTreeNode> {
    const response = await apiClient.post<ResourceTreeNode>('/resources', {
      name,
      parentId,
      isDirectory,
    });
    return response.data;
  }

  /**
   * 创建新目录
   * @param name 目录名称
   * @param parentId 父目录ID，可选
   * @returns Promise<ResourceTreeNode>
   */
  static async createDirectory(name: string, parentId?: number): Promise<ResourceTreeNode> {
    return this.createResource(name, parentId, true);
  }

  /**
   * 创建新本体
   * @param name 本体名称
   * @param parentId 父目录ID，可选
   * @returns Promise<ResourceTreeNode>
   */
  static async createOntology(name: string, parentId?: number): Promise<ResourceTreeNode> {
    return this.createResource(name, parentId, false);
  }

  /**
   * 删除资源
   * @param id 资源ID
   * @param targetDirId 目标目录ID（用于移动子资源）
   * @returns Promise<void>
   */
  static async deleteResource(id: number, targetDirId?: number): Promise<void> {
    const config = targetDirId !== undefined ? {
      data: { targetDirId }
    } : undefined;
    await apiClient.delete(`/resources/${id}`, config);
  }

  /**
   * 重命名资源
   * @param id 资源ID
   * @param name 新名称
   * @returns Promise<ResourceTreeNode>
   */
  static async renameResource(id: number, name: string): Promise<ResourceTreeNode> {
    const response = await apiClient.put<ResourceTreeNode>(`/resources/${id}`, {
      name,
    });
    return response.data;
  }

  /**
   * 移动资源
   * @param sourceId 源资源ID
   * @param targetId 目标资源ID
   * @returns Promise<void>
   */
  static async moveResource(sourceId: number, targetId: number): Promise<void> {
    await apiClient.put(`/resources/${sourceId}/move`, {
      sourceId,
      targetId,
    });
  }
}

export default ResourceApi;